import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isUnloadPromptSuppressed } from "@/shared/utils/reloadWithoutPrompt";
import { DIALOG_PARAMS, guardUnsavedChanges } from "./unsavedChangesGuard";

/**
 * Несохранённые правки в диалоге редактора (автоматизации, email-рассылки,
 * шаблоны писем):
 * - закрытие вкладки и перезагрузка: стандартный вопрос браузера
 *   (`beforeunload`, как у FormBuilder с warnOnUnsavedChanges);
 * - кнопка «Назад» браузера: тот же вопрос, что у крестика диалога
 *   (`window.confirm` с тем же текстом), при отказе адрес возвращается и
 *   диалог остаётся открытым (см. unsavedChangesGuard.ts).
 *
 * Закрытие самого диалога крестиком подтверждает сам диалог: перехват
 * кликов по ссылкам здесь не нужен, крестик DialogHeader тоже ссылка
 * («#close») и спросил бы дважды. Смена адреса самим приложением
 * (pushState, например ?tab=create на ?tab=edit&id= после создания) не
 * popstate и не перехватывается.
 *
 * `params`: параметры адреса, которыми открыт диалог; «Назад», который их
 * не меняет, диалог не закрывает и вопроса не задаёт.
 */
export function useUnsavedChangesGuard(
  dirty: boolean,
  message: string,
  params: readonly string[] = DIALOG_PARAMS
) {
  // Снимок адреса диалога обновляется при каждой смене адреса приложением
  // и берётся у роутера: адрес окна в момент переустановки перехвата уже
  // может быть следующим (см. unsavedChangesGuard.ts).
  const { key: locationKey, pathname, search } = useLocation();
  const paramsKey = params.join(",");

  useEffect(
    () =>
      guardUnsavedChanges(window, {
        dirty,
        message,
        params: paramsKey.split(","),
        isUnloadSuppressed: isUnloadPromptSuppressed,
        dialogLocation: { pathname, search },
      }),
    [dirty, message, locationKey, pathname, search, paramsKey]
  );
}
